import { RelationshipLabel } from '../types/Guests';
export type { RelationshipLabel };
import { apiGet, apiPost, apiPut, apiDelete, planningPath } from './PlanningApiClient';
import { ProjectScopedCache } from './projectScopedCache';

// Admin-managed vocabulary for the guest-relations picker (GuestForm's
// relations editor), backed by ans-backend's relationship_labels table
// (db/repositories/planning/relationshipLabels.ts). Deliberately a separate
// concept from GuestTag/guest_tags — tags are attributes stamped onto a
// guest record; this is just the phrase vocabulary offered for
// guest_relations.relationship_label, which stays a free-text column
// regardless (a one-off label typed in the guest form is just as valid as a
// managed one, it just isn't saved to the catalog for reuse).

// Empty until the API answers: the project's own labels (the nine defaults are seeded server-side on first read).
const DEFAULT_LABELS: RelationshipLabel[] = [];

const labelsCache = new ProjectScopedCache<RelationshipLabel[]>(() => [...DEFAULT_LABELS]);

export const RELATIONSHIP_LABELS_CHANGE_EVENT = 'kalligo_relationship_labels_change';

export const loadRelationshipLabels = (): RelationshipLabel[] => labelsCache.load();

function saveCache(key: string, labels: RelationshipLabel[]): void {
  labelsCache.saveFor(key, labels);
  window.dispatchEvent(new Event(RELATIONSHIP_LABELS_CHANGE_EVENT));
}

function mapApiRow(row: any): RelationshipLabel {
  return {
    id: row.id,
    label: row.label,
    createdAt: row.created_at,
  };
}

export const refreshRelationshipLabelsFromApi = async (): Promise<RelationshipLabel[]> => {
  const key = labelsCache.currentKey();
  try {
    const res = await apiGet<{ labels: any[] }>(planningPath('/relationship-labels'));
    if (Array.isArray(res?.labels)) {
      saveCache(key, res.labels.map(mapApiRow));
    }
  } catch {
    // API unreachable — keep existing in-memory cache
  }
  return loadRelationshipLabels();
};

export const createRelationshipLabelApi = async (label: string): Promise<RelationshipLabel> => {
  const created = await apiPost<any>(planningPath('/relationship-labels'), { label });
  await refreshRelationshipLabelsFromApi();
  return mapApiRow(created);
};

export const updateRelationshipLabelApi = async (id: string, label: string): Promise<RelationshipLabel | null> => {
  const updated = await apiPut<any>(planningPath(`/relationship-labels/${id}`), { label });
  await refreshRelationshipLabelsFromApi();
  return updated ? mapApiRow(updated) : null;
};

export const deleteRelationshipLabelApi = async (id: string): Promise<void> => {
  await apiDelete(planningPath(`/relationship-labels/${id}`));
  await refreshRelationshipLabelsFromApi();
};
